import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// Musi być zgodny z kluczem w skrypcie anty-mrugnięciowym w public/index.html.
const STORAGE_KEY = 'mgdpt-theme';

const THEMES = ['light', 'dark', 'system'];

const ThemeContext = createContext(null);

const readStoredTheme = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
};

const prefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

export function ThemeProvider({ children }) {
  // Stan startowy czytamy z localStorage, żeby był zgodny z klasą `dark`,
  // którą skrypt w index.html ustawił już przed pierwszym renderem.
  const [theme, setThemeState] = useState(readStoredTheme);
  const [systemIsDark, setSystemIsDark] = useState(prefersDark);

  // Nasłuch na zmianę motywu systemowego — istotny tylko w trybie 'system',
  // ale subskrypcja jest tania, więc trzymamy ją bezwarunkowo.
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event) => setSystemIsDark(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const isDark = theme === 'dark' || (theme === 'system' && systemIsDark);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  const setTheme = useCallback((next) => {
    if (!THEMES.includes(next)) return;
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* tryb prywatny — motyw zadziała, po prostu nie przetrwa odświeżenia */
    }
  }, []);

  // Przełączanie w cyklu jasny -> ciemny -> systemowy.
  const cycleTheme = useCallback(() => {
    setTheme(THEMES[(THEMES.indexOf(theme) + 1) % THEMES.length]);
  }, [theme, setTheme]);

  const value = useMemo(
    () => ({ theme, isDark, setTheme, cycleTheme }),
    [theme, isDark, setTheme, cycleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme musi być użyty wewnątrz <ThemeProvider>');
  return context;
}
